import { haversineDistance } from '../utils/geo';

// Baked POI tiles, generated by scripts/extract-tiles.mjs from OSM state
// extracts and served as static files from public/regions/. This keeps the
// app free of any runtime Overpass dependency (slow, rate-limited,
// periodically down) wherever we have coverage; live Overpass remains the
// fallback for uncovered areas (see nearbyService).
const BASE = '/regions';
// The activity feed stays local (what's around me), but discovery deliberately
// reaches further — people will drive 40 minutes for the right swimming hole.
const NEARBY_RADIUS_MI = 15;
export const DISCOVERY_RADIUS_MI = 35;

let manifestPromise = null;
const tileCache = new Map(); // file -> Promise<pois[]>

function loadManifest() {
  if (!manifestPromise) {
    manifestPromise = fetch(`${BASE}/manifest.json`)
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null);
  }
  return manifestPromise;
}

function loadTile(file) {
  if (!tileCache.has(file)) {
    tileCache.set(
      file,
      fetch(`${BASE}/${file}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((tile) => tile?.pois ?? [])
        .catch(() => [])
    );
  }
  return tileCache.get(file);
}

/**
 * Baked POIs within `radiusMi` of a location (default ~15mi for the local
 * feed; pass DISCOVERY_RADIUS_MI to reach further), distances computed from
 * the user's actual position. Returns null when the location has no tile
 * coverage — callers should fall back to live fetching.
 */
export async function getBakedPOIs(lat, lon, radiusMi = NEARBY_RADIUS_MI) {
  const manifest = await loadManifest();
  if (!manifest?.tiles) return null;

  const size = manifest.tileSizeDeg;
  const dLat = radiusMi / 69;
  const dLon = radiusMi / (69 * Math.cos((lat * Math.PI) / 180));

  // Collect grid tiles intersecting the user's radius bbox. Tile keys use
  // the same floor(v / size) * size arithmetic as the generator, and 0.25°
  // is exactly representable, so string keys match without epsilon games.
  const files = [];
  const s0 = Math.floor((lat - dLat) / size) * size;
  const s1 = Math.floor((lat + dLat) / size) * size;
  const w0 = Math.floor((lon - dLon) / size) * size;
  const w1 = Math.floor((lon + dLon) / size) * size;
  for (let s = s0; s <= s1; s += size) {
    for (let w = w0; w <= w1; w += size) {
      const entry = manifest.tiles[`${s}_${w}`];
      if (entry) files.push(entry.file);
    }
  }
  if (!files.length) return null;

  const lists = await Promise.all(files.map(loadTile));
  const pois = lists
    .flat()
    .map((poi) => ({ ...poi, distance: haversineDistance(lat, lon, poi.lat, poi.lon) }))
    .filter((poi) => poi.distance <= radiusMi)
    .sort((a, b) => a.distance - b.distance);

  return pois.length ? pois : null;
}
